import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Download, Trash2, ShieldAlert, X } from "lucide-react";
import { apiExportMyData, apiDeleteMyAccount } from "@/lib/api";

export default function DataRightsModal({ open, onClose, onDeleted }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(null);

  const exportData = async () => {
    setBusy("export");
    try {
      const data = await apiExportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rsell-ai-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Downloaded your data as JSON");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed — please sign in");
    } finally {
      setBusy(null);
    }
  };

  const deleteAccount = async () => {
    setBusy("delete");
    try {
      await apiDeleteMyAccount();
      toast.success("Your account was deleted");
      if (onDeleted) onDeleted();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed — please sign in");
    } finally {
      setBusy(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="data-rights-modal"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-md glass-strong p-6 sm:p-7 rounded-2xl"
          >
            <button onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white/80" aria-label="Close">
              <X size={18} />
            </button>

            <h2 className="font-display text-white text-xl font-semibold mb-1">Your data, your rights</h2>
            <p className="text-white/50 text-xs mb-6">Under GDPR you can download or delete everything we hold on you.</p>

            <button
              type="button"
              data-testid="data-export-btn"
              onClick={exportData}
              disabled={busy === "export"}
              className="w-full flex items-center gap-3 p-4 rounded-xl bg-white/[0.04] border border-white/10 hover:border-[#10B981]/50 hover:bg-white/[0.06] transition-colors text-left disabled:opacity-50"
            >
              <Download size={18} className="text-[#10B981] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-semibold">Download my data</div>
                <div className="text-white/50 text-xs">JSON file with your account, scans, payments and consents.</div>
              </div>
            </button>

            <button
              type="button"
              data-testid="delete-account-btn"
              onClick={() => setConfirmDelete((v) => !v)}
              className="mt-3 w-full flex items-center gap-3 p-4 rounded-xl bg-white/[0.04] border border-white/10 hover:border-red-500/50 hover:bg-red-500/[0.05] transition-colors text-left"
            >
              <Trash2 size={18} className="text-red-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-semibold">Delete my account</div>
                <div className="text-white/50 text-xs">Permanent. Payment records stay 7 years for tax law, but stripped of PII.</div>
              </div>
            </button>

            {confirmDelete && (
              <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/[0.06] p-3.5" data-testid="delete-confirm-block">
                <div className="flex items-start gap-2 mb-3">
                  <ShieldAlert size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/80 text-xs leading-relaxed">
                    This is permanent. Any active Whop subscription must be cancelled separately from your Whop dashboard.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="flex-1 py-2 rounded-lg bg-white/[0.06] text-white/70 text-xs font-semibold hover:bg-white/[0.1] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="delete-confirm-btn"
                    disabled={busy === "delete"}
                    onClick={deleteAccount}
                    className="flex-1 py-2 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
                  >
                    {busy === "delete" ? "Deleting…" : "Yes, delete forever"}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
